import { useParams, useLocation } from "wouter";
import { useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { useNotes, useCreateNote, useUpdateNote, useDeleteNote } from "@/hooks/useNotes";
import { useProject } from "@/hooks/useProjects";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import type { NoteKind } from "@/lib/api";
import { MessageSquare, Plus, MoreHorizontal, Edit2, Trash2 } from "lucide-react";

const NOTE_KIND_LABELS: Record<NoteKind, string> = {
  briefing_call: "Briefing call",
  vendor_qa: "Vendor Q&A",
  addendum: "Addendum received",
  client_call: "Call with client",
  internal_note: "Internal note",
  other: "Other",
};

export default function UpdatesPage() {
  const params = useParams<{ id?: string }>();
  const projectId = params.id!;
  const [, navigate] = useLocation();

  // Load project and notes
  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { notes, loading: notesLoading, error: notesError } = useNotes(projectId);

  // Create note form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createKind, setCreateKind] = useState<NoteKind>("internal_note");
  const [createBody, setCreateBody] = useState("");
  const [createFeedToAi, setCreateFeedToAi] = useState(true);
  const { createNote, loading: createLoading } = useCreateNote(projectId);

  // Delete confirmation
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const { deleteNote, loading: deleteLoading } = useDeleteNote();

  // Edit mode
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const { updateNote, loading: updateLoading } = useUpdateNote();

  const handleCreateNote = async () => {
    if (!createBody.trim()) return;
    const newNote = await createNote({
      kind: createKind,
      body: createBody,
      feedToAi: createFeedToAi,
    });
    if (newNote) {
      setCreateBody("");
      setCreateKind("internal_note");
      setCreateFeedToAi(true);
      setShowCreateForm(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteNoteId) return;
    const success = await deleteNote(deleteNoteId);
    if (success) {
      setDeleteNoteId(null);
    }
  };

  const handleEditNote = async (noteId: string) => {
    if (!editBody.trim()) return;
    await updateNote(noteId, { body: editBody });
    setEditNoteId(null);
  };

  const startEdit = (noteId: string, currentBody: string) => {
    setEditNoteId(noteId);
    setEditBody(currentBody);
  };

  const relativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!project) {
    if (projectLoading) {
      return (
        <Layout projectId={projectId}>
          <PageHeader title="Updates" subtitle="Loading..." />
          <div className="p-8">
            <p style={{ color: "#9CA3AF" }}>Loading updates...</p>
          </div>
        </Layout>
      );
    }
    navigate("/");
    return null;
  }

  return (
    <Layout projectId={projectId} projectTitle={project.title}>
      <PageHeader
        title="Updates"
        subtitle="Notes from briefing calls, addendums, internal updates"
      />

      <div className="p-8 max-w-4xl pb-40 space-y-4">
        {/* Create form */}
        {showCreateForm && (
          <Card>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-white block mb-2">Type</label>
                <select
                  value={createKind}
                  onChange={(e) => setCreateKind(e.target.value as NoteKind)}
                  className="w-full px-3 py-2 rounded-lg text-sm border"
                  style={{
                    backgroundColor: "#1F2937",
                    borderColor: "rgba(229,231,235,0.1)",
                    color: "#F3F4F6",
                  }}
                >
                  {Object.entries(NOTE_KIND_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-white block mb-2">Note</label>
                <textarea
                  value={createBody}
                  onChange={(e) => setCreateBody(e.target.value)}
                  placeholder="Write your note here..."
                  className="w-full px-3 py-2 rounded-lg text-sm border min-h-24"
                  style={{
                    backgroundColor: "#1F2937",
                    borderColor: "rgba(229,231,235,0.1)",
                    color: "#F3F4F6",
                  }}
                />
              </div>

              <label className="flex items-center gap-2 text-sm" style={{ color: "#D1D5DB" }}>
                <input
                  type="checkbox"
                  checked={createFeedToAi}
                  onChange={(e) => setCreateFeedToAi(e.target.checked)}
                  className="rounded"
                />
                Use this in AI drafts
              </label>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ color: "#9CA3AF" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNote}
                  disabled={createLoading || !createBody.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: "#F97316" }}
                >
                  {createLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </Card>
        )}

        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-white transition-all"
            style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" }}
          >
            <Plus size={16} />
            Add Note
          </button>
        )}

        {/* Notes list */}
        {notesError && (
          <Card>
            <p style={{ color: "#EF4444" }}>{notesError}</p>
          </Card>
        )}

        {!notesLoading && notes.length === 0 && !showCreateForm && (
          <Card>
            <div className="flex items-start gap-4 text-center py-8">
              <div className="flex-1">
                <MessageSquare size={24} style={{ color: "#6B7280", margin: "0 auto mb-2" }} />
                <p className="text-sm font-medium text-white">No updates yet.</p>
                <p className="mt-1 text-sm" style={{ color: "#9CA3AF" }}>
                  Capture briefing-call notes, vendor Q&A clarifications, or any context that comes
                  in over the life of this RFP.
                </p>
              </div>
            </div>
          </Card>
        )}

        {notes.map((note) => (
          <Card key={note.id}>
            {editNoteId === note.id ? (
              <div className="space-y-3">
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm border min-h-24"
                  style={{
                    backgroundColor: "#1F2937",
                    borderColor: "rgba(229,231,235,0.1)",
                    color: "#F3F4F6",
                  }}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setEditNoteId(null)}
                    className="px-3 py-1 rounded text-xs font-medium"
                    style={{ color: "#9CA3AF" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleEditNote(note.id)}
                    disabled={updateLoading || !editBody.trim()}
                    className="px-3 py-1 rounded text-xs font-medium text-white transition-all disabled:opacity-50"
                    style={{ backgroundColor: "#F97316" }}
                  >
                    {updateLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-medium px-2 py-1 rounded"
                      style={{
                        backgroundColor: "rgba(249,115,22,0.1)",
                        color: "#F97316",
                      }}
                    >
                      {NOTE_KIND_LABELS[note.kind]}
                    </span>
                    <span style={{ color: "#6B7280" }} className="text-xs">
                      {relativeTime(note.createdAt)}
                    </span>
                    {note.createdByName && (
                      <span style={{ color: "#9CA3AF" }} className="text-xs">
                        by {note.createdByName}
                      </span>
                    )}
                  </div>

                  <button
                    className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                    onClick={(e) => {
                      e.currentTarget.blur();
                      const menu = e.currentTarget.nextElementSibling as HTMLDivElement;
                      if (menu) menu.classList.toggle("hidden");
                    }}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                  <div
                    className="hidden absolute right-8 mt-8 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10"
                    onClick={(evt) => evt.stopPropagation()}
                  >
                    <button
                      onClick={() => startEdit(note.id, note.body)}
                      className="w-full text-left px-4 py-2 text-xs font-medium text-white hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={(evt) => {
                        setDeleteNoteId(note.id);
                        (evt.currentTarget.parentElement as HTMLDivElement)?.classList.add(
                          "hidden",
                        );
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-medium text-red-400 hover:bg-gray-700 flex items-center gap-2 border-t border-gray-700"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>

                <p
                  className="text-sm leading-relaxed mb-3 whitespace-pre-wrap"
                  style={{ color: "#D1D5DB" }}
                >
                  {note.body}
                </p>

                <div className="text-xs" style={{ color: "#6B7280" }}>
                  {note.feedToAi ? "Used by AI" : "Internal only"}
                </div>
              </>
            )}
          </Card>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        open={deleteNoteId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteNoteId(null);
        }}
        title="Delete note?"
        description="This note will be permanently deleted. This action cannot be undone."
        onConfirm={handleDeleteNote}
        confirmLabel="Delete"
      />

      {/* Updates is not in the spine, so we don't render NextStepFooter here */}
    </Layout>
  );
}
